/* 调律诗篇十周年更新蓝图 — 中国版客户端视图。
   此独立版本固定使用简体中文，且不包含语言切换功能。 */

import {
  BUCKETS,
  ROADMAP,
  UI,
  tx,
  type Bullet,
  type BucketKey,
  type Card,
  type Locale,
  type LocaleMap,
  type Month,
} from "./roadmap-data";
import "./roadmap.css";
import "./fonts.css";

const LOCALE: Locale = "zh-CN";
const art = (path: string) => path;
const LOGO = "brand-logo.webp";

/* Fonts are self-hosted and page-subsetted (see build-10th-fonts.mjs). @font-face
   declarations live in the static roadmap.css supplement and, for the portal
   build, in ./fonts.css (imported above). The active data-lang stack references
   only that locale's faces, so no runtime injection is needed. */

/* ─── small inline icons (24×24, 1.5 stroke, currentColor) ───────────── */
function RIcon({
  d,
  size = 16,
  sw = 1.5,
}: {
  d: React.ReactNode;
  size?: number;
  sw?: number;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {d}
    </svg>
  );
}
const IconImage = (p: { size?: number }) => (
  <RIcon
    {...p}
    d={
      <>
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <path d="M21 15l-5-5L5 21" />
      </>
    }
  />
);
const IconCal = (p: { size?: number }) => (
  <RIcon
    {...p}
    d={
      <>
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <path d="M16 2v4M8 2v4M3 10h18" />
      </>
    }
  />
);

/* ─── deco recipes (scattered chibi + note doodles) ──────────────────── */
type DecoKind = "chibi" | "popper" | "ribbon" | "trinket";
interface DecoSlotSpec {
  k: DecoKind;
  size: number;
  rot: number;
  s: React.CSSProperties;
}

const DECO_RECIPES: DecoSlotSpec[][] = [
  [
    { k: "chibi", size: 86, rot: 6, s: { bottom: "-34px", right: "-24px" } },
    { k: "popper", size: 62, rot: -8, s: { bottom: "-30px", right: "42%" } },
  ],
  [
    { k: "chibi", size: 84, rot: -6, s: { bottom: "-32px", right: "-22px" } },
    { k: "ribbon", size: 60, rot: 8, s: { top: "67%", left: "-26px" } },
    { k: "trinket", size: 58, rot: 5, s: { bottom: "-26px", left: "26%" } },
  ],
  [
    { k: "chibi", size: 86, rot: -7, s: { bottom: "-32px", right: "-22px" } },
    { k: "trinket", size: 60, rot: 6, s: { bottom: "-28px", left: "42%" } },
  ],
  [
    { k: "chibi", size: 84, rot: 7, s: { bottom: "-34px", right: "-22px" } },
    { k: "popper", size: 58, rot: -8, s: { top: "67%", left: "-26px" } },
    { k: "ribbon", size: 60, rot: 4, s: { bottom: "-30px", left: "32%" } },
  ],
];

function decoIdx(str: string, mod: number) {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return h % mod;
}

/* SD chibi + note doodle pools — assigned in document order so neither
   repeats until exhausted. A card may pin its chibi via card.chibi. */
const SD_CHIBI = [
  "art/sd/sd-4.png",
  "art/sd/sd-5.png",
  "art/sd/sd-6.png",
  "art/sd/sd-7.png",
  "art/sd/sd-35-1.png",
  "art/sd/sd-35-2.png",
  "art/sd/sd-35-3.png",
  "art/sd/sd-35-4.png",
  "art/sd/sd-35-5.png",
  "art/sd/sd-35-6.png",
  "art/sd/sd-35-7.png",
  "art/sd/sd-26-1.png",
  "art/sd/sd-26-2.png",
  "art/sd/sd-26-3.png",
  "art/sd/sd-26-4.png",
];
const SD_NOTE = [
  "art/deco/d-5.png",
  "art/deco/d-6.png",
  "art/deco/d-7.png",
  "art/deco/d-8.png",
  "art/deco/d-10.png",
  "art/deco/d-11.png",
  "art/deco/d-12.png",
  "art/deco/d-16.png",
  "art/deco/d-21.png",
];

/* Decorative SD chibis perched on the timeline rail — keyed by month index. */
const RAIL_SD: {
  i: number;
  src: string;
  top: string;
  size: number;
  dx: number;
  rot: number;
  flip: boolean;
}[] = [
  {
    i: 0,
    src: "art/sd/event26_1.png",
    top: "30%",
    size: 78,
    dx: -3,
    rot: -6,
    flip: false,
  },
  {
    i: 1,
    src: "art/sd/event26_3.png",
    top: "68%",
    size: 68,
    dx: -5,
    rot: 5,
    flip: true,
  },
  {
    i: 3,
    src: "art/sd/event26_2.png",
    top: "22%",
    size: 74,
    dx: -2,
    rot: -4,
    flip: false,
  },
];

/* Assign distinct chibis + notes to every deco slot across the timeline. */
const CHIBI_ASSIGN: Record<string, string[]> = {};
const NOTE_ASSIGN: Record<string, string[]> = {};
(() => {
  const decoCards: Card[] = [];
  const usedChibi = new Set<string>();
  for (const m of ROADMAP)
    for (const c of m.cards) {
      const hasKeyArt = (c.bucket === "content" || c.keyart) && !c.smallDecor;
      if (hasKeyArt) continue;
      decoCards.push(c);
      if (c.chibi) usedChibi.add(c.chibi);
    }
  let ci = 0,
    ni = 0;
  const nextChibi = () => {
    let s = SD_CHIBI[0],
      guard = 0;
    do {
      s = SD_CHIBI[ci++ % SD_CHIBI.length];
    } while (usedChibi.has(s) && ++guard < SD_CHIBI.length);
    usedChibi.add(s);
    return s;
  };
  for (const c of decoCards) {
    const recipe = DECO_RECIPES[decoIdx(c.id, DECO_RECIPES.length)];
    const chibis: string[] = [],
      notes: string[] = [];
    for (const d of recipe) {
      if (d.k === "chibi") chibis.push(c.chibi || nextChibi());
      else notes.push(SD_NOTE[ni++ % SD_NOTE.length]);
    }
    CHIBI_ASSIGN[c.id] = chibis;
    NOTE_ASSIGN[c.id] = notes;
  }
})();

/* ─── Placeholder key-art block ──────────────────────────────────────── */
function PlaceholderArt({
  kind,
  bucket,
  label,
}: {
  kind: "banner" | "thumb";
  bucket: BucketKey;
  label: string;
}) {
  const b = BUCKETS[bucket];
  return (
    <div
      className={`ph-art ph-${kind}`}
      style={
        {
          "--art-tint": b.tint,
          "--art-line": b.line,
          "--art-color": b.color,
        } as React.CSSProperties
      }
    >
      <span className="ph-corner ph-tl" />
      <span className="ph-corner ph-tr" />
      <span className="ph-corner ph-bl" />
      <span className="ph-corner ph-br" />
      <div className="ph-label">
        <span style={{ color: b.color, display: "inline-flex" }}>
          <IconImage size={kind === "banner" ? 20 : 16} />
        </span>
        <span>{label}</span>
      </div>
    </div>
  );
}

/* ─── Scattered deco layer (chibi + note doodles) ────────────────────── */
function DecoLayer({ card, bucket }: { card: Card; bucket: BucketKey }) {
  const b = BUCKETS[bucket];
  const slots = DECO_RECIPES[decoIdx(card.id, DECO_RECIPES.length)];
  const chibis = CHIBI_ASSIGN[card.id] || [];
  const notes = NOTE_ASSIGN[card.id] || [];
  let ci = 0,
    ni = 0;
  return (
    <div
      className="rc-deco"
      aria-hidden="true"
      style={{ "--deco-line": b.line } as React.CSSProperties}
    >
      {slots.map((d, i) => {
        const isChibi = d.k === "chibi";
        const src = isChibi ? chibis[ci++] : notes[ni++];
        return (
          <div
            className={"deco-slot" + (isChibi ? " is-chibi" : " is-note")}
            key={i}
            style={
              {
                ...d.s,
                width: d.size,
                height: d.size,
                "--rot": `${d.rot}deg`,
              } as React.CSSProperties
            }
          >
            {src && <img src={art(src)} alt="" loading="lazy" />}
          </div>
        );
      })}
    </div>
  );
}

/* ─── Real key-art banner ────────────────────────────────────────────── */
function ArtBanner({
  src,
  kind,
  bucket,
  imgPos,
  imgPosMobile,
  imgAspectMobile,
}: {
  src: string;
  kind: "banner" | "thumb";
  bucket: BucketKey;
  imgPos?: string;
  imgPosMobile?: string;
  imgAspectMobile?: string;
}) {
  const b = BUCKETS[bucket];
  return (
    <div
      className={`rc-art rc-art-${kind}`}
      style={
        {
          "--art-line": b.line,
          ...(imgPos ? { "--art-pos": imgPos } : {}),
          ...(imgPosMobile ? { "--art-pos-m": imgPosMobile } : {}),
          /* mobile-only aspect-ratio override; --art-h-m: auto lets the ratio drive
           height (a fixed banner height would otherwise win over aspect-ratio) */
          ...(imgAspectMobile
            ? { "--art-ar-m": imgAspectMobile, "--art-h-m": "auto" }
            : {}),
        } as React.CSSProperties
      }
    >
      <img className="rc-art-img" src={art(src)} alt="" loading="lazy" />
      <span className="rc-art-fade" aria-hidden="true" />
    </div>
  );
}

/* Normalize a bullet into {label, sub?}. A wrapped bullet has a `t` field;
   a flat bullet is a bare locale map (whose keys are locale codes, never "t"). */
function normBullet(b: Bullet): { label: LocaleMap; sub?: LocaleMap[] } {
  if (b && typeof b === "object" && "t" in b) {
    const pb = b as { t: LocaleMap; sub?: LocaleMap[] };
    return { label: pb.t, sub: pb.sub };
  }
  return { label: b as LocaleMap };
}

/* ─── A single roadmap card ──────────────────────────────────────────── */
function RoadmapCard({
  card,
  lang,
  idx,
}: {
  card: Card;
  lang: Locale;
  idx: number;
}) {
  const b = BUCKETS[card.bucket];
  const featured = !!card.featured;
  const hasKeyArt =
    (card.bucket === "content" || card.keyart) && !card.smallDecor;
  const showBanner = hasKeyArt;
  const showSticker = !hasKeyArt;
  const artKind: "banner" | "thumb" = featured ? "banner" : "thumb";
  const cardCls = featured ? "glass-card-accent" : "glass-card";
  const artLabel = tx(card.shot ? UI.screenshot : UI.keyArt, lang);
  /* pick the locale-specific screenshot when the card provides one, else the
     base image */
  const imgSrc = card.imgL10n?.[lang] ?? card.img;

  return (
    <article
      className={`rc ${cardCls}${featured ? " rc-featured" : ""}${card.smallTitle ? " rc-small-title" : ""}${showSticker ? " rc-has-sticker" : ""} anim-fade-up`}
      style={
        {
          "--bk": b.color,
          "--bk-tint": b.tint,
          "--bk-line": b.line,
          animationDelay: `${0.05 + idx * 0.06}s`,
        } as React.CSSProperties
      }
    >
      {showBanner &&
        (imgSrc ? (
          <ArtBanner
            src={imgSrc}
            kind={artKind}
            bucket={card.bucket}
            imgPos={card.imgPos}
            imgPosMobile={card.imgPosMobile}
            imgAspectMobile={card.imgAspectMobile}
          />
        ) : (
          <PlaceholderArt
            kind={artKind}
            bucket={card.bucket}
            label={artLabel}
          />
        ))}
      {showSticker && <DecoLayer card={card} bucket={card.bucket} />}
      <div className="rc-body">
        <div className="rc-meta">
          <span
            className="rc-chip"
            style={{ color: b.color, background: b.tint, borderColor: b.line }}
          >
            <span className="rc-dot" style={{ background: b.color }} />
            {tx(card.tag, lang)}
          </span>
          {(card.date || card.tba) && (
            <span className={`rc-date${card.tba ? " rc-date-tba" : ""}`}>
              <IconCal size={12} />
              {card.tba ? tx(UI.tba, lang) : card.date}
            </span>
          )}
        </div>
        <h3 className="rc-title">{tx(card.title, lang)}</h3>
        {card.desc && <p className="rc-desc">{tx(card.desc, lang)}</p>}
        {card.tracks && (
          <ul
            className="rc-tracks"
            style={{ "--bk": b.color } as React.CSSProperties}
          >
            {card.tracks.map((tk, i) => (
              <li className="rc-track" key={i}>
                <span className="rc-track-mark" style={{ color: b.color }}>
                  ♪
                </span>
                <span className="rc-track-text">
                  <span className="rc-track-name">{tk.name}</span>
                  <span className="rc-track-sep"> / </span>
                  <span className="rc-track-artist">{tk.artist}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
        {card.tracksCta && (
          <p className="rc-tracks-cta">{tx(card.tracksCta, lang)}</p>
        )}
        {card.trackGroups &&
          card.trackGroups.map((g, gi) => (
            <div className="rc-track-group" key={gi}>
              <p className="rc-track-head">{tx(g.heading, lang)}</p>
              <ul
                className="rc-tracks"
                style={{ "--bk": b.color } as React.CSSProperties}
              >
                {g.tracks.map((tk, i) => (
                  <li className="rc-track" key={i}>
                    <span className="rc-track-mark" style={{ color: b.color }}>
                      ♪
                    </span>
                    <span className="rc-track-text">
                      <span className="rc-track-name">{tk.name}</span>
                      <span className="rc-track-sep"> / </span>
                      <span className="rc-track-artist">{tk.artist}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        {card.note && !card.bullets && (
          <p className="rc-note">{tx(card.note, lang)}</p>
        )}
        {card.period && (
          <p className="rc-period">
            <IconCal size={12} />
            <span className="rc-period-label">{tx(UI.period, lang)}</span>
            <span className="rc-period-time">{card.period}</span>
          </p>
        )}
        {card.bullets &&
          (() => {
            const bullets = card.bullets;
            const rich = bullets.some((bt) => normBullet(bt).sub);
            return (
              <>
                {card.bulletsLead && (
                  <p className="rc-bullets-lead">
                    {tx(card.bulletsLead, lang)}
                  </p>
                )}
                <ul className={`rc-bullets${rich ? " rc-bullets-rich" : ""}`}>
                  {bullets.map((bt, i) => {
                    const { label, sub } = normBullet(bt);
                    return (
                      <li key={i}>
                        {!card.noMark && (
                          <span
                            className="rc-bullet-mark"
                            style={{ color: b.color }}
                          >
                            ◆
                          </span>
                        )}
                        {tx(label, lang)}
                        {sub && (
                          <ul className="rc-subbullets">
                            {sub.map((s, j) => (
                              <li key={j}>
                                <span
                                  className="rc-subbullet-mark"
                                  style={{ color: b.color }}
                                >
                                  ◆
                                </span>
                                {tx(s, lang)}
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    );
                  })}
                </ul>
                {card.coda && (
                  <div className="rc-coda">
                    <p className="rc-coda-lead">{tx(card.coda.lead, lang)}</p>
                    <p className="rc-coda-text">{tx(card.coda.text, lang)}</p>
                  </div>
                )}
                {card.note && (
                  <p className="rc-note rc-note-after">{tx(card.note, lang)}</p>
                )}
              </>
            );
          })()}
      </div>
    </article>
  );
}

/* ─── A month / time-window block ────────────────────────────────────── */
function MonthBlock({
  month,
  lang,
  idx,
}: {
  month: Month;
  lang: Locale;
  idx: number;
}) {
  const sd = RAIL_SD.find((d) => d.i === idx);
  return (
    <section className={`month${month.soon ? " month-soon" : ""}`}>
      <div className="month-rail">
        <span className="month-node" />
        {sd && (
          <img
            className="rail-sd"
            src={art(sd.src)}
            alt=""
            aria-hidden="true"
            loading="lazy"
            style={{
              top: sd.top,
              width: `${sd.size}px`,
              transform: `translateX(calc(-50% + ${sd.dx}px)) rotate(${sd.rot}deg)${sd.flip ? " scaleX(-1)" : ""}`,
            }}
          />
        )}
      </div>
      <div className="month-body">
        <header className="month-head anim-fade-up">
          <div className="month-marker-row">
            <span className="month-marker">{tx(month.marker, lang)}</span>
            {month.year && <span className="month-year">{month.year}</span>}
          </div>
          {(idx === 0 || month.soon) && (
            <h2 className="month-title">{tx(month.title, lang)}</h2>
          )}
        </header>
        <div className="cards">
          {month.cards.map((c, i) => (
            <RoadmapCard key={c.id} card={c} lang={lang} idx={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Hero ───────────────────────────────────────────────────────────── */
function Hero({ lang }: { lang: Locale }) {
  return (
    <header className="hero">
      <div className="hero-inner">
        <div className="hero-copy">
          <h1 className="hero-title">{tx(UI.heroTitle, lang)}</h1>
          <p className="hero-sub">{tx(UI.heroSub, lang)}</p>
        </div>
        <div className="hero-art">
          <span className="hf-tl" />
          <span className="hf-tr" />
          <span className="hf-bl" />
          <span className="hf-br" />
          <img
            className="hero-art-img"
            src={art("art/kv/kv-hero.webp")}
            alt=""
            aria-hidden="true"
          />
          <img className="hero-logo" src={LOGO} alt="调律诗篇" />
        </div>
      </div>
    </header>
  );
}

/* ─── Footer ─────────────────────────────────────────────────────────── */
function FooterBar({ lang }: { lang: Locale }) {
  return (
    <footer className="rm-footer">
      <div className="footer-divider" />
      <div className="footer-mark">调律诗篇</div>
      <div className="footer-row">
        <div className="footer-left">
          <img src={LOGO} alt="调律诗篇" />
          <span className="footer-sep">|</span>
          <span>{tx(UI.footNote, lang)}</span>
        </div>
        <p className="footer-copy">调律诗篇 © Noxy Games Inc.</p>
      </div>
    </footer>
  );
}

/* ─── Page ───────────────────────────────────────────────────────────── */
export default function RoadmapClient() {
  return (
    <div
      className="rm-page"
      data-density="comfortable"
      data-decor="full"
      data-layout="auto"
      data-lang={LOCALE}
      lang="zh-Hans"
    >
      <div className="bg-keyart-blur" />
      <div className="bg-grad" />
      <div className="ambient-glow-1" />
      <div className="ambient-glow-2" />
      <main className="wrap">
        <Hero lang={LOCALE} />
        <div className="timeline">
          {ROADMAP.map((m, i) => (
            <MonthBlock key={m.id} month={m} lang={LOCALE} idx={i} />
          ))}
        </div>
        <FooterBar lang={LOCALE} />
      </main>
    </div>
  );
}
